<template>
  <div class="stories">
    <div class="stories-scroll" ref="scrollContainer">
      <!-- Your Story -->
      <div class="story-item" @click="onMyStoryClick">
        <div class="story-avatar" :class="myStories.length ? 'story-avatar--own' : 'story-avatar--add'">
          <UserAvatar v-if="logged" :item="logged" :size="'28'"/>
          <span v-if="!myStories.length" class="story-add-icon">+</span>
        </div>
        <span class="story-name">Your Story</span>
      </div>

      <!-- Other stories -->
      <div
        v-for="story in otherStories"
        :key="story.id"
        class="story-item"
        @click="viewStory(story)"
      >
        <div class="story-avatar story-avatar--new">
          <UserAvatar :item="getStoryUser(story)" :size="'28'"/>
        </div>
        <span class="story-name">{{ getStoryUserName(story) }}</span>
      </div>
    </div>

    <!-- Create Story Dialog -->
    <v-dialog v-model="showCreate" max-width="360" content-class="story-dialog">
      <v-card class="story-card">
        <div class="story-card-head">
          <span class="story-card-title">Create Story</span>
          <button class="story-card-close" @click="showCreate = false">&times;</button>
        </div>
        <div class="story-card-body">
          <div class="story-upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="onDrop">
            <div v-if="!previewUrl" class="story-upload-placeholder">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8e8e93" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              <span>Tap to add photo or video</span>
            </div>
            <img v-if="previewUrl && !isVideoFile" :src="previewUrl" class="story-upload-preview"/>
            <video v-if="previewUrl && isVideoFile" :src="previewUrl" class="story-upload-preview" muted/>
          </div>
          <input ref="fileInput" type="file" accept="image/*,video/*" hidden @change="onFileSelected"/>
          <div class="story-caption-row">
            <input v-model="storyTitle" placeholder="Add a caption..." class="story-caption-field"/>
          </div>
        </div>
        <div class="story-card-foot">
          <button class="story-btn-cancel" @click="showCreate = false">Cancel</button>
          <button class="story-btn-post" :disabled="!selectedFile || uploading" @click="publishStory">
            <span v-if="!uploading">Post Story</span>
            <span v-else>Posting...</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Story Viewer Modal -->
    <v-dialog v-model="showViewer" max-width="500" overlay-opacity="0.85" content-class="story-viewer-dialog">
      <div class="story-viewer" @click="showViewer = false">
        <!-- Header with user info + close button -->
        <div class="story-viewer-header" v-if="activeStory" @click.stop>
          <UserAvatar :item="getStoryUser(activeStory)" :size="'32'"/>
          <div class="story-viewer-info">
            <span class="story-viewer-name">{{ getStoryFullName(activeStory) }}</span>
            <span class="story-viewer-time">{{ formatStoryTime(activeStory.created_date) }}</span>
          </div>
          <button class="story-viewer-close" @click="showViewer = false">&times;</button>
        </div>
        <!-- Full-screen media -->
        <div class="story-viewer-media" v-if="activeStory" @click.stop="showViewer = false">
          <video
            v-if="isVideo(activeStory)"
            :src="getStoryMedia(activeStory)"
            controls
            autoplay
            class="story-viewer-video"
          ></video>
          <img
            v-else-if="getStoryMedia(activeStory)"
            :src="getStoryMedia(activeStory)"
            class="story-viewer-img"
          />
          <div v-else class="story-viewer-text">
            <h3 v-if="activeStory.title">{{ activeStory.title }}</h3>
            <p v-if="getStoryDescription(activeStory)">{{ getStoryDescription(activeStory) }}</p>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { firestore } from "@/config/firebase";
import { mapState } from "vuex";
import UserAvatar from "@/components/UserAvatar";
import VideoUtilsV2 from "@/util/video_utils_v2";

export default {
  name: "Stories",
  components: { UserAvatar },
  data() {
    return {
      allStories: [],
      storyUsers: {},
      fetchedUserIds: {},
      subscription: null,
      showViewer: false,
      showCreate: false,
      activeStory: null,
      selectedFile: null,
      previewUrl: null,
      isVideoFile: false,
      storyTitle: "",
      uploading: false,
      // Tenant user doc ID resolved on demand — see myUserDocId().
      resolvedUserDoc: null,
    };
  },
  computed: {
    ...mapState({
      tenant_id: (state) => state.Chat.tenant_id,
      owner_id: (state) => state.Chat.owner_id,
      logged: (state) => state.Chat.logged,
      uxDocs: (state) => state.Chat.uxDocs,
    }),
    // A story's `user_id` is the TENANT user doc ID (tenants/{tid}/users/{docId}),
    // not the global Auth UID — iOS matches on `tenantUserID` and Android on
    // `UserEntity.id`, both of which are that doc ID.
    //
    // We resolve this ourselves rather than reading `Chat.user_doc` from the
    // store: that value is only populated by SwitchAccount / QR login /
    // localStorage, and immediately after a tenant switch it can still hold the
    // PREVIOUS tenant's doc ID — which would silently publish a story under the
    // wrong ID. Resolving per tenant is one cached read and always correct.
    myUserDocId() {
      return this.resolvedUserDoc;
    },
    myStories() {
      const id = this.myUserDocId;
      return id ? this.allStories.filter((s) => s.user_id === id) : [];
    },
    otherStories() {
      const id = this.myUserDocId;
      return id ? this.allStories.filter((s) => s.user_id !== id) : this.allStories;
    },
  },
  watch: {
    tenant_id: {
      immediate: true,
      handler(val) {
        // The tenant user doc ID is per-tenant — drop the stale one on switch.
        this.resolvedUserDoc = null;
        if (val) {
          this.ensureUserDocId();
          this.fetchStories();
        }
      },
    },
  },
  methods: {
    // Resolves this user's tenant user doc ID, the ID stories are keyed by.
    async ensureUserDocId() {
      if (this.myUserDocId) return this.myUserDocId;
      if (!this.tenant_id || !this.owner_id) return null;
      try {
        const snap = await firestore
          .collection("tenants")
          .doc(this.tenant_id)
          .collection("users")
          .where("owner_id", "==", this.owner_id)
          .limit(1)
          .get();
        if (snap.docs.length) this.resolvedUserDoc = snap.docs[0].id;
      } catch (e) {
        console.warn("[Stories] Could not resolve tenant user doc id:", e);
      }
      return this.myUserDocId;
    },
    getStoryUser(story) {
      const uid = story.user_id;
      // 1. Check reactive local cache
      if (this.storyUsers[uid]) return this.storyUsers[uid];
      // 2. Check uxDocs (conversation participants cache)
      const cached = this.uxDocs && this.uxDocs[uid];
      if (cached && cached.first_name) {
        this.$set(this.storyUsers, uid, cached);
        return cached;
      }
      // 3. Kick off async fetch if not already fetching
      if (uid && !this.fetchedUserIds[uid]) {
        this.$set(this.fetchedUserIds, uid, true);
        this.fetchUserDoc(uid);
      }
      // 4. Return fallback from story doc fields
      return {
        first_name: story.first_name || "",
        last_name: story.last_name || "",
        profile_picture: story.profile_picture || "",
      };
    },
    getStoryUserName(story) {
      const user = this.getStoryUser(story);
      return user.first_name || story.first_name || "User";
    },
    getStoryFullName(story) {
      const user = this.getStoryUser(story);
      return ((user.first_name || "") + " " + (user.last_name || "")).trim() || "User";
    },
    async fetchUserDoc(userId) {
      try {
        // Story user_id is the tenant user doc ID, not the global Auth UID.
        // Step 1: Get the tenant user doc to find the global owner_id
        let globalUserId = userId;
        if (this.tenant_id) {
          const tenantDoc = await firestore.collection("tenants").doc(this.tenant_id).collection("users").doc(userId).get();
          if (tenantDoc.exists && tenantDoc.data().owner_id) {
            globalUserId = tenantDoc.data().owner_id;
          }
        }

        // Step 2: Check uxDocs with the global owner_id
        const cached = this.uxDocs && this.uxDocs[globalUserId];
        if (cached && cached.first_name) {
          this.$set(this.storyUsers, userId, cached);
          return;
        }

        // Step 3: Fetch from global users collection using owner_id
        const doc = await firestore.collection("users").doc(globalUserId).get();
        if (doc.exists) {
          const data = doc.data();
          const userObj = {
            id: globalUserId,
            owner_id: globalUserId,
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            profile_picture: data.profile_picture || "",
          };
          this.$set(this.storyUsers, userId, userObj);
        }
      } catch (e) {
        console.warn("Failed to fetch user doc for story:", userId, e);
      }
    },
    fetchStories() {
      if (this.subscription) this.subscription();
      if (!this.tenant_id) return;

      this.subscription = firestore
        .collection("tenants")
        .doc(this.tenant_id)
        .collection("updates")
        .orderBy("created_date", "desc")
        .limit(50)
        .onSnapshot(
          (snapshot) => {
            const now = Date.now();
            const grouped = {};
            snapshot.forEach((doc) => {
              const data = { id: doc.id, ...doc.data() };
              let expMs = this.toMs(data.expiration_date);
              if (!expMs) {
                const createdMs = this.toMs(data.created_date);
                expMs = createdMs ? createdMs + 86400000 : 0;
              }
              if (expMs < now) return;
              if (!grouped[data.user_id]) {
                grouped[data.user_id] = data;
              }
            });
            this.allStories = Object.values(grouped);
          },
          () => {
            // Fallback without ordering
            this.fetchStoriesFallback();
          }
        );
    },
    fetchStoriesFallback() {
      if (!this.tenant_id) return;
      if (typeof this.subscription === 'function') this.subscription();
      this.subscription = firestore
        .collection("tenants")
        .doc(this.tenant_id)
        .collection("updates")
        .limit(50)
        .onSnapshot((snapshot) => {
          const now = Date.now();
          const grouped = {};
          snapshot.forEach((doc) => {
            const data = { id: doc.id, ...doc.data() };
            let expMs = this.toMs(data.expiration_date);
            if (!expMs) {
              const createdMs = this.toMs(data.created_date);
              expMs = createdMs ? createdMs + 86400000 : 0;
            }
            if (expMs < now) return;
            if (!grouped[data.user_id]) grouped[data.user_id] = data;
          });
          this.allStories = Object.values(grouped);
        }, error => {
          console.error('[Stories] Fallback updates listener error:', error);
        });
    },
    toMs(val) {
      if (!val) return 0;
      if (val.toMillis) return val.toMillis();
      if (val.seconds) return val.seconds * 1000;
      if (typeof val === "number") return val > 1e12 ? val : val * 1000;
      return 0;
    },
    getStoryMedia(story) {
      return story.media_url || story.video_url || story.image_url || story.url || story.thumbnail_url || "";
    },
    // `description` is the cross-platform field; `details` is the legacy
    // web-only name still present on pre-fix documents.
    getStoryDescription(story) {
      return story.description || story.details || "";
    },
    // Seconds, as mobile writes it. Read from the local preview so we never
    // wait on the network.
    readVideoDuration() {
      return new Promise((resolve) => {
        try {
          const video = document.createElement("video");
          video.preload = "metadata";
          video.onloadedmetadata = () => resolve(Number.isFinite(video.duration) ? video.duration : null);
          video.onerror = () => resolve(null);
          video.src = this.previewUrl;
        } catch (e) {
          resolve(null);
        }
      });
    },
    // Poster frame for video stories. Generated from the LOCAL blob: URL —
    // drawing the remote Storage URL onto a canvas taints it and toDataURL()
    // throws. A failure here must never block publishing.
    async uploadVideoThumbnail() {
      try {
        const dataUrl = await VideoUtilsV2.loadImageFromVideo(this.previewUrl);
        if (!dataUrl) return "";
        const blob = await (await fetch(dataUrl)).blob();
        const thumbFile = new File([blob], "thumbnail.jpg", { type: blob.type || "image/jpeg" });
        return await VideoUtilsV2.uploadFileToStorage(thumbFile, "updates", this.tenant_id);
      } catch (e) {
        console.warn("[Stories] Could not generate video thumbnail:", e);
        return "";
      }
    },
    isVideo(story) {
      const url = this.getStoryMedia(story);
      return url.match(/\.(mp4|mov|webm|avi|m4v|mkv|flv|wmv)/i) || story.media_type === "video" || story.type === "video";
    },
    onMyStoryClick() {
      if (this.myStories.length) {
        this.viewStory(this.myStories[0]);
      } else {
        this.showCreate = true;
      }
    },
    viewStory(story) {
      this.activeStory = story;
      this.showViewer = true;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileSelected(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.selectedFile = file;
      this.isVideoFile = file.type.startsWith("video/");
      this.previewUrl = URL.createObjectURL(file);
    },
    onDrop(e) {
      const file = e.dataTransfer.files[0];
      if (!file) return;
      this.selectedFile = file;
      this.isVideoFile = file.type.startsWith("video/");
      this.previewUrl = URL.createObjectURL(file);
    },
    async publishStory() {
      if (!this.selectedFile || !this.tenant_id) {
        console.warn("publishStory: missing file or tenant_id", { file: !!this.selectedFile, tenant_id: this.tenant_id });
        return;
      }
      this.uploading = true;

      try {
        // Stories are keyed by the tenant user doc ID on every client. Without it
        // the story would be unattributable on mobile, so fail loudly instead of
        // publishing an orphan.
        const userDocId = await this.ensureUserDocId();
        if (!userDocId) {
          throw new Error("Could not resolve your profile for this team. Please reload and try again.");
        }

        // Upload to Firebase Storage via the shared helper, so stories land at
        // updates/{tenantId}/{uuid}.{ext} like every other upload. The object name
        // used to be `{owner_id}_{timestamp}` — guessable, and it leaked the owner
        // id into the path.
        const mediaUrl = await VideoUtilsV2.uploadFileToStorage(
          this.selectedFile,
          "updates",
          this.tenant_id
        );

        const isVideoStory = this.isVideoFile;
        let thumbnailUrl = mediaUrl;
        let duration = null;
        if (isVideoStory) {
          duration = await this.readVideoDuration();
          thumbnailUrl = await this.uploadVideoThumbnail();
        }

        // This document is read by iOS and Android too, so it MUST use their
        // schema (see DATABASE_SCHEMA.md → tenants/{tenantId}/updates).
        //
        // The dates are epoch-millisecond NUMBERS, not Firestore Timestamps. iOS
        // range-filters its listener with `whereField("expiration_date",
        // isGreaterThan: <ms number>)`; a Timestamp sorts into a different type
        // group and never matches, so Timestamp-dated stories were invisible on
        // iOS entirely. iOS also decodes these as `Double` and throws a type
        // mismatch on a Timestamp. Keep them numeric.
        const nowMs = Date.now();
        const storyData = {
          user_id: userDocId,
          url: mediaUrl,
          thumbnail_url: thumbnailUrl,
          type: isVideoStory ? "video" : "image",
          title: this.storyTitle || "",
          description: null,
          duration: duration,
          created_date: nowMs,
          start_date: null,
          expiration_date: nowMs + 86400000,
          viewed_by: {},
        };

        await firestore
          .collection("tenants")
          .doc(this.tenant_id)
          .collection("updates")
          .add(storyData);

        // Both mobile clients stamp this on the author's tenant user doc; iOS
        // uses it to light up the "new story" ring. Non-fatal — by this point the
        // story is already published.
        try {
          await firestore
            .collection("tenants")
            .doc(this.tenant_id)
            .collection("users")
            .doc(userDocId)
            .update({ updates_creation_date: nowMs });
        } catch (e) {
          console.warn("[Stories] Could not stamp updates_creation_date:", e);
        }

        this.showCreate = false;
        this.selectedFile = null;
        this.previewUrl = null;
        this.storyTitle = "";
        this.isVideoFile = false;
      } catch (err) {
        console.error("Error publishing story:", err);
        alert("Failed to post story: " + err.message);
      } finally {
        this.uploading = false;
      }
    },
    formatStoryTime(date) {
      const ms = this.toMs(date);
      if (!ms) return "";
      const d = new Date(ms);
      const diff = Date.now() - d;
      if (diff < 3600000) return Math.floor(diff / 60000) + "m ago";
      if (diff < 86400000) return Math.floor(diff / 3600000) + "h ago";
      return d.toLocaleDateString();
    },
  },
  beforeDestroy() {
    if (this.subscription) this.subscription();
  },
};
</script>

<style lang="scss" scoped>
@import '../_variables';

.stories {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 300px;
  margin: 0 4px;
}

.stories-scroll {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

.story-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover .story-avatar { transform: scale(1.05); }
}

.story-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  padding: 2px;
  background: $t4;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: transform 0.15s;

  &::v-deep .v-avatar {
    border: 1.5px solid #fff !important;
    border-radius: 50%;
  }

  &--new {
    background: linear-gradient(135deg, #3478f6, #00c6ff);
  }

  &--own {
    background: linear-gradient(135deg, #3478f6, #00c6ff);
  }

  &--add {
    background: transparent;
    border: 1.5px dashed $t3;
  }
}

.story-add-icon {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 13px;
  height: 13px;
  background: $accent;
  color: #fff;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #fff;
  line-height: 1;
}

.story-name {
  font-size: 9px;
  color: $t2;
  font-family: $font;
  font-weight: 500;
  max-width: 44px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

// Shrink stories at smaller widths
@media (max-width: 768px) {
  .stories { max-width: 200px; }
  .stories-scroll { gap: 4px; }
  .story-avatar { width: 28px; height: 28px; }
  .story-name { font-size: 7px; max-width: 32px; }
  .story-add-icon { width: 10px; height: 10px; font-size: 7px; }
}

@media (max-width: 600px) {
  .stories { max-width: 140px; }
  .story-avatar { width: 24px; height: 24px;
    &::v-deep .v-avatar { width: 20px !important; height: 20px !important; }
  }
  .story-name { display: none; }
}

@media (max-width: 480px) {
  .stories { max-width: 100px; margin: 0 2px; }
  .stories-scroll { gap: 3px; }
  .story-avatar { width: 20px; height: 20px;
    &::v-deep .v-avatar { width: 16px !important; height: 16px !important; }
  }
  .story-add-icon { width: 8px; height: 8px; font-size: 6px; border-width: 1px; }
}

@media (max-width: 360px) {
  .stories { display: none; }
}

// Create Story Dialog
.story-card {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12) !important;
}

.story-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid $border;
}

.story-card-title {
  font-size: 13px;
  font-weight: 600;
  color: $t1;
  font-family: $font;
}

.story-card-close {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  font-size: 18px;
  color: $t3;
  cursor: pointer;
  border-radius: 50%;
  &:hover { background: rgba(0,0,0,0.06); }
}

.story-card-body {
  padding: 12px 14px;
}

.story-upload-area {
  width: 100%;
  height: 180px;
  border: 1.5px dashed $border-strong;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  background: #fafafa;
  transition: border-color 0.15s;
  &:hover { border-color: $accent; }
}

.story-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  span { font-size: 11px; color: $t3; font-family: $font; }
}

.story-upload-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.story-caption-row {
  margin-top: 10px;
}

.story-caption-field {
  width: 100%;
  height: 28px;
  background: $bg-input;
  border: none;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 11px;
  font-family: $font;
  color: $t1;
  outline: none;
  &::placeholder { color: $t3; }
}

.story-card-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 14px 12px;
}

.story-btn-cancel {
  padding: 4px 12px;
  border: none;
  background: transparent;
  font-size: 11px;
  font-weight: 500;
  color: $t3;
  font-family: $font;
  cursor: pointer;
  border-radius: 6px;
  &:hover { background: rgba(0,0,0,0.04); color: $t1; }
}

.story-btn-post {
  padding: 5px 14px;
  border: none;
  background: $accent;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  font-family: $font;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: default; }
}

// Story Viewer — image-sized popup, no container box
.story-viewer {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.story-viewer-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 0 8px;
  width: 100%;
  &::v-deep .v-avatar { border: none !important; }
}

.story-viewer-close {
  margin-left: auto;
  width: 28px;
  height: 28px;
  background: rgba(0, 0, 0, 0.08);
  border: none;
  border-radius: 50%;
  color: $t1;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover { background: rgba(0, 0, 0, 0.15); }
}

.story-viewer-info { display: flex; flex-direction: column; }

.story-viewer-name {
  font-size: 13px;
  font-weight: 600;
  color: $t1;
  font-family: $font;
}

.story-viewer-time {
  font-size: 11px;
  color: $t3;
  font-family: $font;
}

.story-viewer-media {
  display: flex;
  align-items: center;
  justify-content: center;
}

.story-viewer-video {
  max-width: 100%;
  max-height: 75vh;
  border-radius: 10px;
}

.story-viewer-img {
  max-width: 100%;
  max-height: 75vh;
  border-radius: 10px;
}

.story-viewer-text {
  padding: 40px 24px;
  text-align: center;
  color: #fff;
  font-family: $font;
  h3 { font-size: 20px; font-weight: 600; margin-bottom: 12px; }
  p { font-size: 14px; line-height: 1.5; opacity: 0.8; }
}
</style>

<style lang="scss">
@import '../_variables';

.story-viewer-dialog {
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
  border-radius: 0 !important;
}
</style>
